import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, ArrowRight, ArrowLeft, Loader2, Package, FileText, Bell, BarChart3, Users, CreditCard, Smartphone, Shield, Clock, CheckCircle, Sun, Moon, X, ShoppingCart, Globe, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import PlatformChatBot from "@/components/PlatformChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// High-Quality Logo Component
const BusinessPortalLogo = ({ size = 48, darkMode = true }) => {
  const primaryColor = darkMode ? "#60A5FA" : "#3B82F6";
  const secondaryColor = darkMode ? "#A78BFA" : "#8B5CF6";
  
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 48 48" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="logoGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={primaryColor} />
          <stop offset="100%" stopColor="#818CF8" />
        </linearGradient>
        <linearGradient id="logoGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={secondaryColor} />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="4" fill="url(#logoGrad1)" filter="url(#glow)" />
      <rect x="26" y="2" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" filter="url(#glow)" />
      <rect x="2" y="26" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" filter="url(#glow)" />
      <rect x="26" y="26" width="20" height="20" rx="4" fill="url(#logoGrad1)" filter="url(#glow)" />
      <line x1="22" y1="12" x2="26" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="12" y1="22" x2="12" y2="26" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="36" y1="22" x2="36" y2="26" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
      <line x1="22" y1="36" x2="26" y2="36" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
};

// Animated Background Component
const AnimatedBackground = ({ darkMode }) => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Animated gradient orbs */}
      <div 
        className={`absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 animate-pulse
          ${darkMode ? 'bg-blue-600' : 'bg-blue-400'}`}
        style={{ 
          top: '-10%', 
          left: '-10%',
          animation: 'float1 15s ease-in-out infinite'
        }} 
      />
      <div 
        className={`absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15 
          ${darkMode ? 'bg-purple-600' : 'bg-purple-400'}`}
        style={{ 
          top: '50%', 
          right: '-5%',
          animation: 'float2 18s ease-in-out infinite'
        }} 
      />
      <div 
        className={`absolute w-[400px] h-[400px] rounded-full blur-3xl opacity-10 
          ${darkMode ? 'bg-indigo-500' : 'bg-indigo-300'}`}
        style={{ 
          bottom: '-10%', 
          left: '30%',
          animation: 'float3 20s ease-in-out infinite'
        }} 
      />
      
      {/* CSS Keyframes */}
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, 50px) scale(1.05); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, -30px) scale(1.1); }
          66% { transform: translate(20px, -50px) scale(0.9); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, -20px) scale(1.05); }
          66% { transform: translate(-30px, 40px) scale(0.95); }
        }
      `}</style>
    </div>
  );
};

const features = [
  {
    icon: Package,
    title: "Live Inventory Tracking",
    description: "Sold something? It's automatically out of stock. No manual updates.",
    details: {
      headline: "Never Run Out of Stock Again",
      bullets: [
        "Automatic stock updates when you create invoices",
        "Low stock alerts sent directly to your email",
        "Track cost vs. selling price for profit analysis",
        "Organize by categories (brakes, filters, engine parts, etc.)",
        "Set minimum stock levels for each item",
        "Lock prices on specific items to protect from markup changes",
        "Bulk import inventory from CSV files"
      ],
      benefit: "Keep your shelves stocked and customers happy"
    }
  },
  {
    icon: FileText,
    title: "Professional Invoicing",
    description: "Create, send, and track invoices. Accept payments online or in-person.",
    details: {
      headline: "Get Paid Faster with Professional Invoices",
      bullets: [
        "Create invoices in under 60 seconds",
        "Auto-calculate taxes based on your settings",
        "Email invoices directly to customers with PDF attachment",
        "Track paid, pending, and partial payments",
        "Record multiple payment methods per invoice",
        "Accept Apple Pay & Google Pay online",
        "Tax-exempt option for resellers and non-profits"
      ],
      benefit: "Look professional and get paid on time"
    }
  },
  {
    icon: Bell,
    title: "Smart Notifications",
    description: "Low stock alerts, payment reminders, and follow-up notifications.",
    details: {
      headline: "Never Miss an Important Update",
      bullets: [
        "Email alerts when inventory runs low",
        "Payment confirmation notifications",
        "Password reset and account security alerts",
        "Stay informed without constantly checking the app",
        "Customize which alerts you want to receive"
      ],
      benefit: "Peace of mind knowing nothing slips through"
    }
  },
  {
    icon: BarChart3,
    title: "Real-Time Insights",
    description: "Revenue, profit margins, pending payments - all at a glance.",
    details: {
      headline: "Make Smarter Business Decisions",
      bullets: [
        "Dashboard shows total revenue at a glance",
        "Track profit from parts (cost vs. selling price)",
        "See pending payments that need follow-up",
        "Sales tax collected - ready for tax season",
        "Tax-exempt sales tracking for audits",
        "Filter by date range (month, quarter, year)",
        "Export reports to CSV for your accountant"
      ],
      benefit: "Make data-driven decisions with confidence"
    }
  },
  {
    icon: Users,
    title: "Customer Management",
    description: "Track customers, vehicles, service history, and contact info.",
    details: {
      headline: "Build Stronger Customer Relationships",
      bullets: [
        "Store customer contact info and addresses",
        "Track vehicle details (make, model, VIN, truck number)",
        "View complete payment history per customer",
        "See all invoices for each customer",
        "Mark customers as tax-exempt with reason",
        "Add notes and special instructions",
        "Bulk import customers from CSV"
      ],
      benefit: "Build loyalty and repeat business"
    }
  },
  {
    icon: CreditCard,
    title: "Multiple Payment Options",
    description: "Square Terminal, online payments, Zelle, cash - your choice.",
    details: {
      headline: "Accept Payments Your Way",
      bullets: [
        "Square Terminal for in-person card payments",
        "Record cash and check payments",
        "Zelle QR code display on invoices",
        "Track partial payments on large invoices",
        "Edit payment method after recording",
        "Payment history tracked per invoice",
        "Support for multiple payment providers"
      ],
      benefit: "Flexible options for every customer"
    }
  }
];

const benefits = [
  { icon: Smartphone, text: "Works on any device - phone, tablet, or computer" },
  { icon: Shield, text: "Your data is secure and backed up automatically" },
  { icon: Clock, text: "Set up in minutes, not days" },
  { icon: CheckCircle, text: "24/7 access to your business data" }
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  
  // Use global theme context - dark mode is default
  const darkMode = theme === "dark";

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    // Helper: cold-start can hit Cloudflare's __cf_bm bot challenge,
    // which fails silently the first time then works on retry. We do
    // ONE auto-retry on network-level failures (no response object) so
    // the user doesn't have to manually refresh.
    const attemptLogin = async (allowRetry = true) => {
      try {
        return await axios.post(`${API}/auth/login`, {
          email: email.toLowerCase().trim(),
          password: password,
        }, { timeout: 12000 });
      } catch (err) {
        const transient = !err.response && allowRetry;
        if (transient) {
          await new Promise(r => setTimeout(r, 400));
          return attemptLogin(false);
        }
        throw err;
      }
    };
    try {
      const response = await attemptLogin(true);
      
      // Clear any old cached data first
      localStorage.removeItem("app_user");
      
      // Build userData with all fields including business_category for routing
      const userData = {
        id: response.data.user_id,
        name: response.data.name,
        email: response.data.email,
        phone: response.data.phone || '',
        role: response.data.role,
        business_id: response.data.business_id,
        business_slug: response.data.business_slug,
        business_name: response.data.business_name,
        business_category: response.data.business_category || null,
        // CRITICAL: pass the JWT through so AuthContext.login() can
        // persist it and set the axios Authorization default header.
        // Without this, subsequent authenticated calls (e.g. setting
        // the Financials PIN) get treated as anonymous.
        access_token: response.data.access_token,
      };
      
      // Store user in context and localStorage
      login(userData);

      // BELT-AND-BRACES: also write the token directly here, BEFORE any
      // navigation, in case a downstream setter / React batching race
      // would otherwise leave `app_token` empty after the full-page
      // reload below. AuthContext.login() does this too, but a previous
      // testing-agent run caught localStorage.app_token coming up null
      // for some users — this guarantees the token is on disk by the
      // time window.location.href fires.
      if (response.data?.access_token) {
        try {
          localStorage.setItem("app_token", response.data.access_token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.access_token}`;
        } catch { /* private mode etc — non-fatal */ }
      }
      
      toast.success(`Welcome back, ${response.data.name}!`);
      
      // Determine target URL based on user role and business category
      let targetUrl;
      if (response.data.role === "super_admin") {
        targetUrl = "/admin";
      } else if (response.data.business_slug) {
        if (response.data.business_category === "grocery_store") {
          targetUrl = `/${response.data.business_slug}/grocery`;
        } else if (response.data.business_category === "wholesale") {
          targetUrl = `/${response.data.business_slug}/wholesale`;
        } else if (response.data.business_category === "pressure_washing") {
          targetUrl = `/${response.data.business_slug}/pressure-washing`;
        } else {
          targetUrl = `/${response.data.business_slug}`;
        }
      } else {
        toast.error("No business associated with this account");
        return;
      }
      
      // Force a full page reload to clear any cached state
      window.location.href = targetUrl;
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Special-case: backend returns 403 with detail="EMAIL_NOT_VERIFIED" when
      // a user signs up but hasn't clicked the verify link yet. Offer to resend.
      if (error.response?.status === 403 && detail === "EMAIL_NOT_VERIFIED") {
        try {
          await axios.post(`${API}/auth/resend-verification`, { email: email.toLowerCase().trim() });
          toast.error("Please verify your email first. We just sent you a fresh verification link.", { duration: 6000 });
        } catch (e) {
          toast.error("Please verify your email first — check your inbox for the link we sent at signup.", { duration: 6000 });
        }
        return;
      }
      toast.error(detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  // Theme classes (renamed to avoid conflict with context)
  const themeClasses = {
    bg: darkMode 
      ? "bg-slate-950" 
      : "bg-slate-100",
    cardBg: darkMode 
      ? "bg-slate-800/40 border-slate-700/50 hover:border-slate-600/70 hover:bg-slate-800/60" 
      : "bg-white/80 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md",
    formBg: darkMode 
      ? "bg-slate-900/80 border-slate-700/60 backdrop-blur-xl" 
      : "bg-white/90 border-slate-200 shadow-2xl backdrop-blur-xl",
    inputBg: darkMode 
      ? "bg-slate-800/80 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20" 
      : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20",
    headingText: darkMode ? "text-white" : "text-slate-900",
    subText: darkMode ? "text-slate-400" : "text-slate-600",
    labelText: darkMode ? "text-slate-300" : "text-slate-700",
    iconColor: darkMode ? "text-blue-400" : "text-blue-600",
    benefitIcon: darkMode ? "text-emerald-400" : "text-emerald-600",
    benefitText: darkMode ? "text-slate-300" : "text-slate-700",
    linkColor: darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700",
  };

  // Prevent flash of wrong theme
  if (!mounted) {
    return (
      <div className="min-h-screen bg-slate-950" />
    );
  }

  return (
    <div className={`min-h-screen flex relative ${themeClasses.bg} transition-colors duration-500`} data-testid="login-page">
      {/* Animated Background */}
      <AnimatedBackground darkMode={darkMode} />
      
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 z-50 p-3 rounded-full transition-all duration-300 ${
          darkMode 
            ? 'bg-slate-800/80 hover:bg-slate-700 text-yellow-400 border border-slate-700' 
            : 'bg-white hover:bg-slate-50 text-slate-700 shadow-lg border border-slate-200'
        }`}
        data-testid="theme-toggle"
      >
        {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Left Side - Features */}
      <div className="hidden lg:flex lg:w-[58%] p-8 flex-col justify-center relative z-10">
        {/* Logo and Brand */}
        <div className="flex items-center gap-3 mb-6">
          <BusinessPortalLogo size={44} darkMode={darkMode} />
          <div>
            <h1 className={`text-xl font-bold ${themeClasses.headingText}`}>My Business Portal</h1>
            <p className={`${themeClasses.subText} text-xs`}>Your Complete Business Management Solution</p>
          </div>
        </div>

        {/* Main Headline */}
        <div className="mb-8">
          <h2 className={`text-3xl lg:text-4xl font-bold ${themeClasses.headingText} leading-tight mb-2`}>
            Everything your business needs.
          </h2>
          <h3 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            One powerful platform.
          </h3>
          <p className={`${themeClasses.subText} mt-4 text-base max-w-xl leading-relaxed`}>
            Stop juggling spreadsheets and multiple apps. Run your entire operation from a single dashboard.
          </p>
        </div>

        {/* Feature Grid - More compact */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {features.map((feature, index) => (
            <div 
              key={index} 
              onClick={() => setSelectedFeature(feature)}
              className={`rounded-lg p-3 border transition-all duration-300 cursor-pointer hover:scale-[1.02] ${themeClasses.cardBg}`}
            >
              <div className="flex items-start gap-2">
                <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}`}>
                  <feature.icon className={`w-4 h-4 ${themeClasses.iconColor}`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${themeClasses.headingText} text-xs`}>{feature.title}</h4>
                  <p className={`${themeClasses.subText} text-xs mt-0.5 leading-snug line-clamp-2`}>{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Row - More compact */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <benefit.icon className={`w-3 h-3 ${themeClasses.benefitIcon} flex-shrink-0`} />
              <span className={`${themeClasses.benefitText} text-xs`}>{benefit.text}</span>
            </div>
          ))}
        </div>

        {/* Explore Demos Link - Clean single link */}
        <div className="pt-4 border-t border-slate-700/30">
          <Link to="/demos" className="block">
            <div className={`p-3 rounded-lg border transition-all duration-300 hover:scale-[1.01] cursor-pointer flex items-center justify-between ${
              darkMode 
                ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700/50 hover:border-purple-500' 
                : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:border-purple-400'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-800/50' : 'bg-purple-100'}`}>
                  <Eye className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-slate-800'}`}>Explore Demos</p>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Wholesale, Grocery, Washing & more</p>
                </div>
              </div>
              <ArrowRight className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
          </Link>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[42%] flex flex-col items-center justify-center p-8 lg:p-12 relative z-10">
        {/* Back Arrow - Mobile */}
        <div className="w-full max-w-md mb-4 lg:hidden">
          <Link to="/">
            <Button variant="ghost" size="sm" className={`px-2 ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}>
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
          </Link>
        </div>
        
        <div className={`w-full max-w-md rounded-2xl border p-8 lg:p-10 transition-all duration-500 ${themeClasses.formBg}`}>
          {/* Logo for Login Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="mb-4">
              <BusinessPortalLogo size={56} darkMode={darkMode} />
            </div>
            <h2 className={`text-2xl font-bold ${themeClasses.headingText}`}>My Business Portal</h2>
            <p className={`${themeClasses.subText} mt-1 text-center`}>Sign in to your business dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className={`${themeClasses.labelText} font-medium`}>Email Address</Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.subText}`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@business.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 h-12 ${themeClasses.inputBg} transition-all duration-200`}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={`${themeClasses.labelText} font-medium`}>Password</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.subText}`} />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 h-12 ${themeClasses.inputBg} transition-all duration-200`}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 hover:from-blue-500 hover:via-purple-500 hover:to-blue-500 text-white font-semibold text-base shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-purple-500/40 hover:scale-[1.02]"
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <div className="text-center mt-3">
              <Link to="/forgot-password" className={`text-sm ${themeClasses.linkColor} hover:underline`}>
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-center">
            <Link to="/signup">
              <Button
                variant="outline"
                className={`text-lg px-8 py-3 h-auto font-bold ${
                  darkMode 
                    ? 'border-purple-500 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300' 
                    : 'border-purple-500 text-purple-600 hover:bg-purple-50'
                } transition-all duration-300 hover:scale-105`}
              >
                🚀 Try it FREE!
              </Button>
            </Link>
            <p className={`${themeClasses.subText} text-sm mt-3 opacity-70`}>
              NO CREDIT CARD REQUIRED • FAST & EASY
            </p>
            <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-slate-700/50' : 'border-slate-200'} flex justify-center gap-4 text-xs`}>
              <Link to="/privacy" className={`${themeClasses.linkColor} hover:underline`}>Privacy Policy</Link>
              <span className={darkMode ? 'text-slate-600' : 'text-slate-300'}>|</span>
              <Link to="/terms" className={`${themeClasses.linkColor} hover:underline`}>Terms of Service</Link>
              <span className={darkMode ? 'text-slate-600' : 'text-slate-300'}>|</span>
              <a href="mailto:support@mybusinessportal.info" className={`${themeClasses.linkColor} hover:underline`}>Contact Support</a>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Detail Modal */}
      <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className={`max-w-lg ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-3 text-xl ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              {selectedFeature && (
                <>
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-blue-100'}`}>
                    <selectedFeature.icon className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  {selectedFeature.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeature?.details && (
            <div className="space-y-4 mt-2">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {selectedFeature.details.headline}
              </h3>
              
              <ul className="space-y-2">
                {selectedFeature.details.bullets.map((bullet, idx) => (
                  <li key={idx} className={`flex items-start gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                    <span className="text-sm">{bullet}</span>
                  </li>
                ))}
              </ul>
              
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'}`}>
                <p className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  💡 {selectedFeature.details.benefit}
                </p>
              </div>
              
              <Link to="/signup" onClick={() => setSelectedFeature(null)}>
                <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white mt-2">
                  Get Started Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PlatformChatBot />
    </div>
  );
}
